import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import { parseIterationResponse, IterationAlert } from '../../services/iterationResponse';

interface Props {
  strategyId?: string;
  onComplete: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface CanalPrincipal {
  canal: string;
  viene_de_z: string;
  rol_en_z: string;
  rol_en_este_accionable: string;
  justificacion: string;
}

interface CanalComplementario {
  canal: string;
  motivo_inclusion: string;
  relacion_audiencia: string;
  riesgo_dispersion: string;
  condicion_uso: string;
}

interface RolYModoInversion {
  canal: string;
  rol_estrategico: string;
  modo_inversion: string;
  porcentaje_sugerido: number;
  justificacion: string;
}

interface Tarea {
  titulo: string;
  explicacion: string;
  direccion_integrada: string;
  canales_principales: CanalPrincipal[];
  canales_complementarios?: CanalComplementario[];
  rol_y_modo_inversion: RolYModoInversion[];
  objetivos: string;
  kpis: string[];
  justificacion_integracion_z: string;
}

interface ActionablesState {
  tareas: Tarea[];
}

const parseMaybeJSON = (value: any): any => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const countMainChannels = (tasks: Tarea[]): number => {
  const channels = new Set<string>();
  tasks.forEach((task) => {
    (task.canales_principales || []).forEach((channel) => {
      if (channel.canal) channels.add(channel.canal.trim().toLowerCase());
    });
  });
  return channels.size;
};

const Module6BActionables: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [dataState, setDataState] = useState<ActionablesState | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [zDiagnostic, setZDiagnostic] = useState<any>(null);
  const [guardrailAlerts, setGuardrailAlerts] = useState<IterationAlert[]>([]);
  const [exceptionRequest, setExceptionRequest] = useState<{
    message: string;
    type: string;
    justification: string;
  } | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const fetchActionables = async (runProcess = false) => {
    try {
      if (runProcess) {
        const res = await api.post(`/strategies/${strategyId}/modules/13/process`);
        setZDiagnostic(parseMaybeJSON(res.data.module_z_diagnostic));
        const data = res.data.module_6b_actionables;
        if (data) {
          setDataState(parseMaybeJSON(data));
        }
      } else {
        const res = await api.get(`/strategies/${strategyId}`);
        setZDiagnostic(parseMaybeJSON(res.data.module_z_diagnostic));
        const data = res.data.module_6b_actionables;
        if (data) {
          setDataState(parseMaybeJSON(data));
        } else {
          await fetchActionables(true);
        }
      }
    } catch (err) {
      console.error(err);
      alert('Error al generar los accionables integrados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActionables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendChatMessage = async (
    userMsg: string,
    exception?: { type: string; justificacion: string },
    appendUserMessage = true
  ): Promise<boolean> => {
    if (!userMsg.trim() || loading) return false;

    setChatMessage('');
    if (appendUserMessage) {
      setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    } else if (exception) {
      setMessages(prev => [...prev, {
        role: 'user',
        content: `Reintento con ${exception.type === 'imposibilidad_legal' ? 'imposibilidad legal' : 'imposibilidad técnica'}: ${exception.justificacion}`,
      }]);
    }
    setLoading(true);

    try {
      const res = await api.post(`/strategies/${strategyId}/modules/13/chat`, {
        message: userMsg,
        ...(exception ? { guardrail_exception: exception } : {}),
      });

      const result = parseIterationResponse<any>(res.data);

      if (result.state && Array.isArray(result.state.tareas)) {
        setDataState(prev => ({
          ...(prev || {}),
          ...result.state,
          tareas: result.state.tareas,
        }));
      }

      setGuardrailAlerts(result.alertDetails);
      const blocking = result.alertDetails.some((a) => a.severity === 'blocking');
      const needsException = result.alertDetails.some((a) => a.code === 'CANAL_Z_ELIMINADO');

      if (needsException && !exception) {
        setExceptionRequest({ message: userMsg, type: 'imposibilidad_tecnica', justification: '' });
      } else if (!blocking) {
        setExceptionRequest(null);
      }

      const replyText = result.reply || 'He aplicado los cambios directamente en los accionables. Puedes ver el resultado actualizado en los bloques de arriba.';
      setMessages(prev => [...prev, { role: 'assistant', content: replyText }]);
      return !blocking;
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error al actualizar los accionables.' }]);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSendChat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatMessage.trim() || loading) return;
    await sendChatMessage(chatMessage);
  };

  const submitException = async () => {
    if (!exceptionRequest || !exceptionRequest.justification.trim() || loading) return;
    const ok = await sendChatMessage(
      exceptionRequest.message,
      { type: exceptionRequest.type, justificacion: exceptionRequest.justification.trim() },
      false
    );
    if (ok) setExceptionRequest(null);
  };

  const handleTaskFieldChange = (taskIdx: number, field: string, value: any) => {
    if (!dataState) return;
    const updated = { ...dataState };
    if (field === 'kpis') {
      updated.tareas[taskIdx].kpis = value.split(',').map((s: string) => s.trim());
    } else {
      (updated.tareas[taskIdx] as any)[field] = value;
    }
    setDataState(updated);
  };

  const handleNestedFieldChange = (
    taskIdx: number,
    arrayField: 'canales_principales' | 'canales_complementarios' | 'rol_y_modo_inversion',
    itemIdx: number,
    nestedField: string,
    value: any
  ) => {
    if (!dataState) return;
    const updated = { ...dataState };
    const arr = (updated.tareas[taskIdx] as any)[arrayField] || [];
    if (arr[itemIdx]) {
      const item = arr[itemIdx] as any;
      if (nestedField === 'porcentaje_sugerido') {
        item[nestedField] = Number(value);
      } else {
        item[nestedField] = value;
      }
    }
    setDataState(updated);
  };

  const handleSave = async () => {
    if (!dataState || loading) return;
    setLoading(true);
    try {
      // Guardar el estado final y finalizar la estrategia en el backend
      const res = await api.put(`/strategies/${strategyId}/modules/13/update-state`, dataState);
      if (res.status === 200) {
        onComplete();
      }
    } catch (err) {
      console.error(err);
      alert('Error al guardar los accionables');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dataState) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        El Agente ACCION INTEGRADA está cruzando creatividad, target y diagnóstico de inversión para generar tus accionables...
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Módulo 6-B: Accionables Integrados</h2>
      <p style={styles.description}>
        Tareas estratégicas de comunicación ejecutables por los equipos de creatividad y medios de manera alineada.
      </p>

      {zDiagnostic && (() => {
        const subinvested = String(zDiagnostic.clasificacion || '').toLowerCase().includes('subinvertida');
        const taskCount = dataState?.tareas?.length || 0;
        const channelCount = dataState?.tareas ? countMainChannels(dataState.tareas) : 0;
        return (
          <div style={styles.guardrailPanel}>
            <div>
              <strong>Diagnóstico Z:</strong> {zDiagnostic.clasificacion || 'Sin clasificación'}
            </div>
            <div style={styles.guardrailStats}>
              <span>Accionables: {taskCount}{subinvested ? '/3' : ''}</span>
              <span>Canales principales: {channelCount}{subinvested ? '/4' : ''}</span>
              <span>Complementarios: máx. 10%</span>
            </div>
          </div>
        );
      })()}

      {guardrailAlerts.length > 0 && (
        <div style={styles.alertBox}>
          {guardrailAlerts.map((alert, i) => (
            <div key={i} style={{ marginBottom: '6px' }}>
              <strong>{alert.severity === 'blocking' ? 'Bloqueado: ' : 'Aviso: '}</strong>
              {alert.message}
            </div>
          ))}
        </div>
      )}

      {exceptionRequest && (
        <div style={styles.exceptionBox}>
          <h4 style={{ margin: '0 0 8px 0' }}>Declarar imposibilidad para retirar un canal principal de Z</h4>
          <p style={{ ...styles.bodyText, marginBottom: '8px' }}>
            Para eliminar un canal principal de Z necesitamos una justificación técnica o legal demostrable.
          </p>
          <select
            style={styles.exceptionSelect}
            value={exceptionRequest.type}
            onChange={(e) => setExceptionRequest({ ...exceptionRequest, type: e.target.value })}
          >
            <option value="imposibilidad_tecnica">Imposibilidad técnica</option>
            <option value="imposibilidad_legal">Imposibilidad legal</option>
          </select>
          <textarea
            style={styles.exceptionTextarea}
            rows={3}
            placeholder="Explica la imposibilidad..."
            value={exceptionRequest.justification}
            onChange={(e) => setExceptionRequest({ ...exceptionRequest, justification: e.target.value })}
          />
          <div style={styles.exceptionActions}>
            <button
              style={styles.sendBtn}
              onClick={submitException}
              disabled={loading || !exceptionRequest.justification.trim()}
            >
              Reintentar con esta justificación
            </button>
            <button style={styles.secondaryBtn} onClick={() => setExceptionRequest(null)} disabled={loading}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {dataState && dataState.tareas && dataState.tareas.map((t, idx) => (
        <div key={idx} style={styles.card}>
          <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Tarea {idx + 1}: Título</label>
          <input
            type="text"
            style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px', fontFamily: 'inherit' }}
            value={t.titulo || ''}
            onChange={(e) => handleTaskFieldChange(idx, 'titulo', e.target.value)}
          />
          
          <div style={styles.section}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Explicación</label>
            <textarea
              style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
              rows={3}
              value={t.explicacion || ''}
              onChange={(e) => handleTaskFieldChange(idx, 'explicacion', e.target.value)}
            />
          </div>

          <div style={styles.section}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Dirección Integrada (Creatividad + Medios)</label>
            <textarea
              style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
              rows={3}
              value={t.direccion_integrada || ''}
              onChange={(e) => handleTaskFieldChange(idx, 'direccion_integrada', e.target.value)}
            />
          </div>

          <div style={styles.section}>
            <h4 style={styles.subTitle}>Canales Principales (Recomendados por Z)</h4>
            <div style={styles.list}>
              {t.canales_principales.map((c, i) => (
                <div key={i} style={styles.listItem}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Canal</label>
                      <input
                        type="text"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={c.canal || ''}
                        onChange={(e) => handleNestedFieldChange(idx, 'canales_principales', i, 'canal', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Rol en Z</label>
                      <input
                        type="text"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={c.rol_en_z || ''}
                        onChange={(e) => handleNestedFieldChange(idx, 'canales_principales', i, 'rol_en_z', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Rol en Accionable</label>
                      <input
                        type="text"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={c.rol_en_este_accionable || ''}
                        onChange={(e) => handleNestedFieldChange(idx, 'canales_principales', i, 'rol_en_este_accionable', e.target.value)}
                      />
                    </div>
                  </div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Justificación</label>
                  <textarea
                    style={{ padding: '8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px', resize: 'vertical' }}
                    rows={2}
                    value={c.justificacion || ''}
                    onChange={(e) => handleNestedFieldChange(idx, 'canales_principales', i, 'justificacion', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {t.canales_complementarios && t.canales_complementarios.length > 0 && (
            <div style={styles.section}>
              <h4 style={styles.subTitle}>Canales Complementarios Adicionales</h4>
              <div style={styles.list}>
                {t.canales_complementarios.map((c, i) => (
                  <div key={i} style={styles.listItem}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Canal</label>
                        <input
                          type="text"
                          style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                          value={c.canal || ''}
                          onChange={(e) => handleNestedFieldChange(idx, 'canales_complementarios', i, 'canal', e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Afinidad Target</label>
                        <input
                          type="text"
                          style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                          value={c.relacion_audiencia || ''}
                          onChange={(e) => handleNestedFieldChange(idx, 'canales_complementarios', i, 'relacion_audiencia', e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Riesgo Dispersión</label>
                        <input
                          type="text"
                          style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                          value={c.riesgo_dispersion || ''}
                          onChange={(e) => handleNestedFieldChange(idx, 'canales_complementarios', i, 'riesgo_dispersion', e.target.value)}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Motivo de Inclusión</label>
                        <textarea
                          style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px', resize: 'vertical' }}
                          rows={2}
                          value={c.motivo_inclusion || ''}
                          onChange={(e) => handleNestedFieldChange(idx, 'canales_complementarios', i, 'motivo_inclusion', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={styles.section}>
            <h4 style={styles.subTitle}>Modo y Distribución de Inversión</h4>
            <div style={styles.list}>
              {t.rol_y_modo_inversion.map((rmi, i) => (
                <div key={i} style={styles.listItem}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Canal</label>
                      <input
                        type="text"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={rmi.canal || ''}
                        onChange={(e) => handleNestedFieldChange(idx, 'rol_y_modo_inversion', i, 'canal', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Rol Estratégico</label>
                      <input
                        type="text"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={rmi.rol_estrategico || ''}
                        onChange={(e) => handleNestedFieldChange(idx, 'rol_y_modo_inversion', i, 'rol_estrategico', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Modo Inversión</label>
                      <input
                        type="text"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={rmi.modo_inversion || ''}
                        onChange={(e) => handleNestedFieldChange(idx, 'rol_y_modo_inversion', i, 'modo_inversion', e.target.value)}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Porcentaje (%)</label>
                      <input
                        type="number"
                        style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px' }}
                        value={rmi.porcentaje_sugerido || 0}
                        onChange={(e) => handleNestedFieldChange(idx, 'rol_y_modo_inversion', i, 'porcentaje_sugerido', e.target.value)}
                      />
                    </div>
                  </div>
                  <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Justificación</label>
                  <textarea
                    style={{ padding: '8px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '12px', marginTop: '4px', resize: 'vertical' }}
                    rows={2}
                    value={rmi.justificacion || ''}
                    onChange={(e) => handleNestedFieldChange(idx, 'rol_y_modo_inversion', i, 'justificacion', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div style={styles.grid}>
            <div style={styles.gridCol}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Objetivo</label>
              <textarea
                style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
                rows={3}
                value={t.objetivos || ''}
                onChange={(e) => handleTaskFieldChange(idx, 'objetivos', e.target.value)}
              />
            </div>
            <div style={styles.gridCol}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>KPIs de Medición (separados por coma)</label>
              <input
                type="text"
                style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '13px', fontFamily: 'inherit' }}
                value={Array.isArray(t.kpis) ? t.kpis.join(', ') : t.kpis || ''}
                onChange={(e) => handleTaskFieldChange(idx, 'kpis', e.target.value)}
              />
            </div>
          </div>

          <div style={{ ...styles.section, borderTop: '1px solid var(--border)', paddingTop: '12px', marginTop: '12px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--accent)', marginBottom: '4px' }}>Filtro de Viabilidad Competitiva (Z)</label>
            <textarea
              style={{ padding: '10px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'var(--bg-card)', color: 'var(--text-primary)', width: '100%', fontSize: '13px', resize: 'vertical', fontFamily: 'inherit', fontStyle: 'italic' }}
              rows={2}
              value={t.justificacion_integracion_z || ''}
              onChange={(e) => handleTaskFieldChange(idx, 'justificacion_integracion_z', e.target.value)}
            />
          </div>
        </div>
      ))}

      {/* Chat de Iteración */}
      <div style={styles.chatSection}>
        <h4 style={styles.chatTitle}>¿Deseas iterar o modificar los accionables con el Agente Acción Integrada?</h4>
        <div style={styles.chatContainer}>
          <div style={styles.messageList}>
            {messages.length === 0 && (
              <div style={styles.emptyChat}>
                Pídele al agente cosas como "modifica el accionable 2 para enfocarlo más en TV", "agrega un nuevo accionable de conversión" o "sintetiza la explicación de la primera tarea".
              </div>
            )}
            {messages.map((m, idx) => (
              <div 
                key={idx} 
                style={{
                  ...styles.messageWrapper,
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div 
                  style={{
                    ...styles.messageBubble,
                    background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-hover)',
                    color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form style={styles.chatForm} onSubmit={handleSendChat}>
            <input 
              style={styles.chatInput} 
              value={chatMessage} 
              onChange={e => setChatMessage(e.target.value)}
              placeholder="Escribe tus instrucciones de ajuste..." 
              disabled={loading}
            />
            <button style={styles.sendBtn} type="submit" disabled={loading || !chatMessage.trim()}>
              Enviar
            </button>
          </form>
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <button style={styles.submitBtn} onClick={handleSave} disabled={loading}>
          {loading ? 'Guardando...' : 'Aprobar Accionables y Finalizar'}
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '20px' },
  title: { fontSize: '24px', color: 'var(--text-primary)', fontWeight: 600, margin: 0 },
  description: { fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '8px' },
  card: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: 'var(--shadow)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  cardTitle: { fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px 0' },
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  subTitle: { fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' },
  bodyText: { fontSize: '14px', color: 'var(--text-primary)', margin: 0, lineHeight: 1.6 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '4px' },
  gridCol: { display: 'flex', flexDirection: 'column', gap: '8px' },
  bulletList: { paddingLeft: '20px', margin: 0, fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6 },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  listItem: { background: 'var(--bg-hover)', border: '1px solid var(--border)', padding: '12px 16px', borderRadius: '6px', fontSize: '13px', color: 'var(--text-primary)' },
  listJustif: { marginTop: '6px', color: 'var(--text-secondary)', fontSize: '12px', fontStyle: 'italic' },
  guardrailPanel: {
    background: 'var(--box-blue-bg)',
    border: '1px solid var(--box-blue-border)',
    borderRadius: '8px',
    padding: '14px 18px',
    color: 'var(--box-blue-text)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    fontSize: '14px'
  },
  guardrailStats: { display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '13px', color: 'var(--text-secondary)' },
  alertBox: {
    background: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.45)',
    borderRadius: '8px',
    padding: '12px 16px',
    color: 'var(--text-primary)',
    fontSize: '13px'
  },
  exceptionBox: {
    background: 'var(--bg-hover)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  exceptionSelect: {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg-card)',
    color: 'var(--text-primary)',
    fontSize: '14px'
  },
  exceptionTextarea: {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg-card)',
    color: 'var(--text-primary)',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  exceptionActions: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  secondaryBtn: {
    background: 'transparent',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    padding: '10px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '14px'
  },
  chatSection: { marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '20px' },
  chatTitle: { margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' },
  chatContainer: {
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-card)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '250px'
  },
  messageList: { flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
  emptyChat: { color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', marginTop: '40px', fontStyle: 'italic' },
  messageWrapper: { display: 'flex', width: '100%' },
  messageBubble: { padding: '10px 16px', borderRadius: '16px', maxWidth: '75%', fontSize: '14px', lineHeight: 1.4 },
  chatForm: { display: 'flex', borderTop: '1px solid var(--border)', background: 'var(--bg-card)' },
  chatInput: { flex: 1, padding: '12px 16px', border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '14px', outline: 'none' },
  sendBtn: { background: 'var(--accent)', color: '#fff', border: 'none', padding: '0 20px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' },
  submitBtn: {
    background: 'var(--success)',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    boxShadow: 'var(--shadow)',
    transition: 'opacity 0.2s'
  }
};

export default Module6BActionables;
